import React, { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
// import { Bookmark, BookmarkCheck } from 'lucide-react';
import "./SimpleJobCard.scss";

import { ReactComponent as MapPin } from "../../../../assets/images/location-detail.svg";
import { ReactComponent as Clock } from "../../../../assets/images/clock.svg";
import { Copy, Send, Users } from "lucide-react";
import { toast } from "sonner";
import CreatePostModal from "../../../community/components/create-post-modal/CreatePostModal";
import useCategories from "../../../community/hooks/useCategories";
import usePosts from "../../../community/hooks/usePosts";

import { ReactComponent as BookmarkCheck } from "../../../../assets/images/bookmarkcheck.svg";
import { ReactComponent as Bookmark } from "../../../../assets/images/bookmark.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  applyJob,
  jobTracking,
  toggleBookmark,
} from "../../../../redux/slices/jobsSlice";
import {
  FaFacebookF,
  FaLinkedinIn,
  FaWhatsapp,
  FaXTwitter,
} from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import jobViewService from "../../../../api/jobViewService";
import RestrictionModal from "../../../restriction-modals/RestrictionModal";

const SimpleJobCard = ({
  job,
  categories,
  categoriesLoading,
  showBookmarks,
  selectedRole,
  selectedJobType,
  selectedShiftType,
  // Only the first card in the list sets this, so the guided tour has a
  // single stable target for its per-card save/apply/share steps.
  tour = false,
}) => {
  const dispatch = useDispatch();
  const hasJobMeta = Boolean(job?.promoted_by || job?.source_name);
  const navigate = useNavigate();

  const [shareModal, setShareModal] = useState(false);
  const [showCreatePostModal, setShowCreatePostModal] = useState(false);
  const [preFilledContent, setPreFilledContent] = useState("");
  const [jobListingCategory, setJobListingCategory] = useState(null);
  const [jobViewLimitInfo, setJobViewLimitInfo] = useState(null);
  const [showSaveJobModal, setShowSaveJobModal] = useState(false);

  // Community hooks for post creation
  // const { categories, loading: categoriesLoading } = useCategories();
  const { createPost } = usePosts();

  // Find and set Job Listing category (ID 9) when categories are loaded
  useEffect(() => {
    if (categories && categories.length > 0) {
      // Find category with ID 9 (Job Listing)
      const jobCategory = categories.find((cat) => cat.id === 9);
      if (jobCategory) {
        setJobListingCategory(jobCategory);
      }
    }
  }, [categories]);

  // const formatSalary = (salary) => {
  //   if (!salary) return "Salary not disclosed";

  //   const min = Number(salary.min).toLocaleString();
  //   const max = Number(salary.max).toLocaleString();

  //   return `$${min} - $${max}/${salary.period}`;
  // };
  const formatSalary = (salary) => {
    if (!salary || (!salary.min && !salary.max)) {
      return "Salary not listed";
    }

    const min = salary.min ? Number(salary.min).toLocaleString() : null;

    const max = salary.max ? Number(salary.max).toLocaleString() : null;

    if (min && max) {
      return `$${min} - $${max}/${salary.period}`;
    }

    if (min) {
      return `From $${min}/${salary.period}`;
    }

    if (max) {
      return `Up to $${max}/${salary.period}`;
    }

    return "Compensation not disclosed";
  };

  const renderSalary = (salary) => {
    const text = formatSalary(salary);
    const slashIdx = text.lastIndexOf("/");
    if (slashIdx === -1) {
      return text;
    }
    return (
      <>
        {text.slice(0, slashIdx)}
        <span className="salary-period">{text.slice(slashIdx)}</span>
      </>
    );
  };

  const formatbonus = (bonus) => {
    if (!bonus) {
      return "Not listed";
    }

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      // Optional: Use 'narrowSymbol' to ensure it just shows $
      currencyDisplay: "symbol",
      // Usually, salaries don't show cents. You can toggle this:
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(bonus);
  };

  const buildJobSeoUrl = (job) => {
    const titleSlug = createSlug(job?.title);

    return titleSlug
      ? `/job-detail/${titleSlug}-${job?.id}`
      : `/job-detail/${job?.id}`;
  };

  const shareUrl = () => {
    // Generate SEO slug for the auth job-detail route (/job-detail/:slug)
    return `${window.location.origin}${buildJobSeoUrl(job)}`;
  };

  // Generate formatted job content for community post
  const generateJobPostContent = () => {
    const jobTitle = job?.title || "Job Opportunity";
    const companyName = job?.company_name || job?.company?.name || "Company";
    const salary = formatSalary(job?.salary);

    // Handle both location formats
    const city = job?.location_city || job?.location?.city;
    const state = job?.location_state || job?.location?.state;
    const location =
      city && state ? `${city}, ${state}` : "Location not specified";

    const applicationUrl = job?.application_url || "";
    const websiteUrl = `${window.location.origin}/jobs`;

    let content = ` Job Opportunity: ${jobTitle}\n\n`;
    content += ` Company: ${companyName}\n`;
    content += ` Salary: ${salary}\n`;
    content += ` Location: ${location}\n`;

    if (job?.work_model) {
      content += ` Work Mode: ${job.work_model}\n`;
    }
    if (job?.job_type) {
      content += ` Job Type: ${job.job_type}\n`;
    }
    if (job?.shift_type) {
      content += ` Shift: ${job.shift_type}\n`;
    }

    if (job?.description) {
      content += `\n Description:\n${job.description}\n`;
    }

    if (applicationUrl) {
      content += `\nApply here: ${applicationUrl}\n`;
    }
    return content;
  };

  const timeAgo = (date) => {
    if (!date) return "";

    const now = Date.now();
    const past = new Date(date).getTime();
    const diffMs = now - past;

    const seconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    // Just now
    if (seconds < 60) return "Just now";

    // Minutes
    if (minutes < 60) return minutes === 1 ? "1 min ago" : `${minutes} min ago`;

    // Hours
    if (hours < 24) return hours === 1 ? "1 hour ago" : `${hours} hours ago`;

    // Weeks (ONLY exact multiples, up to 4 weeks)
    if (days >= 7 && days <= 28 && days % 7 === 0) {
      const weeks = days / 7;
      return weeks === 1 ? "1 week ago" : `${weeks} weeks ago`;
    }

    // Days (everything else under 30)
    if (days < 30) return days === 1 ? "1 day ago" : `${days} days ago`;

    // Months
    if (months < 12)
      return months === 1 ? "1 month ago" : `${months} months ago`;

    // Years
    return years === 1 ? "1 year ago" : `${years} years ago`;
  };
  // const handleShareClick = () => {
  //   setShareModal(true);
  // };
  const handleShareClick = (e) => {
    e.stopPropagation(); // important
    setShareModal(true);
  };

  const closeShareModal = () => {
    setShareModal(false);
  };

  // const shareToSocialMedia = (platform) => {
  //   // Share the job application link with promotional text for MapMyPay
  //   const applicationUrl = getApplyUrl(job?.application_url);

  //   if (!applicationUrl || applicationUrl === "#") {
  //     toast.error("No application link available to share");
  //     return;
  //   }

  //   const jobTitle = job?.title || "this job opportunity";
  //   const companyName = job?.company?.name || "Company";
  //   const websiteUrl = `${window.location.origin}/jobs`;

  //   // Include promotional text to drive traffic to MapMyPay
  //   const shareText = `Check out ${jobTitle} at ${companyName}. Visit ${websiteUrl} for more such job listings!`;
  //   const shareUrl = applicationUrl;

  //   let url = "";
  //   switch (platform) {
  //     case "twitter":
  //       url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
  //         shareText,
  //       )}&url=${encodeURIComponent(shareUrl)}`;
  //       break;
  //     case "facebook":
  //       url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
  //         shareUrl,
  //       )}`;
  //       break;
  //     case "whatsapp":
  //       url = `https://wa.me/?text=${encodeURIComponent(
  //         shareText + " " + shareUrl,
  //       )}`;
  //       break;
  //     case "linkedin":
  //       url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
  //         shareUrl,
  //       )}`;
  //       break;
  //     case "copy":
  //       navigator.clipboard
  //         .writeText(shareUrl)
  //         .then(() => {
  //           toast.success("Link copied to clipboard!");
  //         })
  //         .catch(() => {
  //           toast.error("Failed to copy link");
  //         });
  //       closeShareModal();
  //       return;
  //     default:
  //       return;
  //   }

  //   if (url) {
  //     const popup = window.open(
  //       url,
  //       "_blank",
  //       "width=600,height=400,scrollbars=yes,resizable=yes",
  //     );
  //     if (!popup) {
  //       toast.warning("Please allow popups for this site to share content");
  //     }
  //     closeShareModal();
  //   }
  // };
  const shareToSocialMedia = (platform) => {
    const applicationUrl = shareUrl();

    const jobTitle = job?.title || "this job opportunity";
    const companyName = job?.company_name || job?.company?.name || "Company";

    const shareText = `Check out ${jobTitle} at ${companyName}`;

    let url = "";

    switch (platform) {
      case "twitter":
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          shareText,
        )}&url=${encodeURIComponent(applicationUrl)}`;
        break;

      case "facebook":
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          applicationUrl,
        )}`;
        break;

      case "whatsapp":
        url = `https://wa.me/?text=${encodeURIComponent(
          shareText + " " + applicationUrl,
        )}`;
        break;

      case "linkedin":
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
          applicationUrl,
        )}`;
        break;

      case "copy":
        navigator.clipboard
          .writeText(applicationUrl)
          .then(() => toast.success("Link copied to clipboard!"))
          .catch(() => toast.error("Failed to copy link"));
        closeShareModal();
        return;

      default:
        return;
    }

    if (url) {
      window.open(
        url,
        "_blank",
        "width=600,height=400,scrollbars=yes,resizable=yes",
      );
      closeShareModal();
    }
  };

  const avatarTextColors = [
    "0E2A4F", // rgb(14, 42, 79)
    "006A4E", // rgb(0, 106, 78)
    "A02021", // rgb(160, 32, 33)
  ];
  const avatarBgColor = "E8F4FB";

  const getAvatarTextColor = (key) => {
    let hash = 0;
    const str = String(key || "Company");

    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }

    return avatarTextColors[Math.abs(hash) % avatarTextColors.length];
  };
  const avatarTextColor = getAvatarTextColor(
    job?.id || job?.company_name || job?.company?.name,
  );

  const createSlug = (text = "") => {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  };
  // const handleBookmark = (jobId) => {
  //   dispatch(toggleBookmark({ job_id: jobId }));
  // };
  const handleBookmark = async (jobId) => {
    try {
      const res = await dispatch(toggleBookmark({ job_id: jobId })).unwrap();

      const isBookmarked = res?.data?.bookmarked;

      // ⭐ If we are in bookmark mode and user removed bookmark
      if (showBookmarks && !isBookmarked) {
        // remove card immediately
        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("removeBookmarkedJob", { detail: jobId }),
          );
        }, 50);
      }
    } catch (err) {
      if (err?.status === 403) {
        setShowSaveJobModal(true);
      } else {
        console.error("Bookmark toggle failed:", err);
      }
    }
  };
  const savedJobIds = useSelector((state) => state.jobResults.savedJobIds);

  const isSaved = savedJobIds.includes(job.id);
  return (
    <>
      {/* Blue Header with Icon and Bookmark */}

      {/* <div className="contentwrap"> */}
      <div
        className="contentwrap mmp-job-card"
        data-tour={tour ? "jobs-jobcard" : undefined}
        onClick={async (e) => {
          // Only navigate if not clicking on a button or modal
          if (
            e.target.closest("button") ||
            e.target.closest(".modal-backdrop-overlay")
          ) {
            return;
          }

          if (!job?.id) return;

          try {
            await jobViewService.record(job.id);
          } catch (err) {
            const body = err?.response?.data;
            if (
              err?.response?.status === 403 &&
              body?.errorType === "JOB_VIEW_LIMIT_REACHED"
            ) {
              setJobViewLimitInfo({
                planSlug: body?.data?.plan_slug || "free",
                used: body?.data?.used ?? 5,
                limit: body?.data?.limit ?? 5,
              });
              return;
            }
          }

          navigate(`/job-detail/${job.id}`);
        }}
        style={{ cursor: "pointer" }}
      >
        <div
          className={`job-card-header ${
            !hasJobMeta ? "job-card-header--no-meta" : ""
          }`}
        >
          <div className="job-icon">
            <img
              // src={
              //   job?.profile_photo
              //     ? `${process.env.REACT_APP_API_BASE_URL}/uploads/job_photos/${job.profile_photo}`
              //     : `https://ui-avatars.com/api/?name=${encodeURIComponent(
              //         job?.company_name || job?.company?.name || "Company",
              //       )}&background=${avatarBg}&color=fff&size=100&bold=true`
              // }
              src={
                job?.profile_photo
                  ? job?.profile_photo.startsWith("http")
                    ? job?.profile_photo // ✅ already full URL → use as is
                    : `${process.env.REACT_APP_API_BASE_URL}/uploads/job_photos/${job?.profile_photo}` // ✅ local path
                  : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                      job?.company_name || job?.company?.name || "Company",
                    )}&background=${avatarBgColor}&color=${avatarTextColor}&size=100&bold=true`
              }
              alt={job?.company_name || job?.company?.name || "Company"}
              className="user-avatar"
              onError={(e) => {
                const target = e.target;
                target.onerror = null;
                target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                  job?.company_name || job?.company?.name || "Company",
                )}&background=${avatarBgColor}&color=${avatarTextColor}&size=100&bold=true`;
              }}
            />
          </div>

          <div className="job-title-description">
            <div className="title-popular">
              <div className="d-flex align-items-center justify-content-between">
                {/* Left side: Hospital Name */}
                <div className="job-title ">
                  {job?.company_name || job?.company?.name}
                </div>
                <div className="d-flex align-items-center gap-2 badge-container">
                  <div className="job-badges">
                    {job?.is_featured && (
                      <span className="badge featured-badge">Featured</span>
                    )}

                    {job?.source === "hospital" && !job?.is_featured && (
                      <span
                        className={`badge ${
                          job?.apply_count > 5
                            ? "hospital-badge hospital-with-popular"
                            : "hospital-badge hospital-alone"
                        }`}
                      >
                        Hospital
                      </span>
                    )}

                    {/* {job?.apply_count > 5 && (
                      <span className="badge populars">Popular</span>
                    )} */}
                  </div>

                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 bookmark-btn"
                    data-tour={tour ? "jobs-jc-save" : undefined}
                    // onClick={() => handleBookmark(job.id)}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBookmark(job.id);
                    }}
                    aria-label={isSaved ? "Remove from saved jobs" : "Save job"}
                  >
                    {isSaved ? (
                      <BookmarkCheck size={16} className="text-warning" />
                    ) : (
                      <Bookmark size={16} className="text-secondary" />
                    )}
                  </Button>
                </div>
              </div>
              <div className="job-type">{job?.title}</div>
            </div>

            {/* <div className="job-description">
              {job.promoted_by && (
                <div className="tag">
                  <div className="tag-desc">Promoted by {job.promoted_by}</div>
                </div>
              )}
              {job?.source_name && (
                <span className="job-desc-text">
                  Responses managed off {job?.source_name}
                </span>
              )}
            </div> */}
          </div>
        </div>
        <div className="job-location">
          <MapPin size={14} />
          {(job?.location_city || job?.location?.city) &&
          (job?.location_state || job?.location?.state)
            ? `${job?.location_city ||
                job?.location?.city}, ${job?.location_state ||
                job?.location?.state}`
            : "Location not specified"}
        </div>
        <div className="job-salary">
          {" "}
          {renderSalary(
            job?.salary || {
              min: job?.salary_min,
              max: job?.salary_max,
              period: job?.salary_period,
            },
          )}
        </div>
        <div className="content">
          <div className="job-meta">
            <span className="posted-time">
              <Users size={12} className="posted-time-icon" />
              <strong>{job?.apply_count}</strong> people clicked apply
            </span>
            <span className="job-schedule">
              <Clock size={14} />
              <span>{timeAgo(job?.posted_date)} </span>
            </span>
          </div>

          {/* <div className="job-desc">{job?.description}</div> */}
          <div className="job-meta-grid">
            <div className="meta-item">
              <span className="meta-label">Bonus</span>
              <span className="meta-value">{formatbonus(job?.bonus)}</span>
            </div>

            {job?.min_education && (
              <div className="meta-item">
                <span className="meta-label">Min Education</span>
                <span className="meta-value">{job?.min_education}</span>
              </div>
            )}

            {job?.required_license_type && (
              <div className="meta-item">
                <span className="meta-label">Required License</span>
                <span className="meta-value">{job?.required_license_type}</span>
              </div>
            )}

            {job?.min_years_experience && (
              <div className="meta-item">
                <span className="meta-label">Min Years Experience</span>
                <span className="meta-value">{job?.min_years_experience}</span>
              </div>
            )}

            {job?.specialty && (
              <div className="meta-item">
                <span className="meta-label">Specialty</span>
                <span className="meta-value">{job?.specialty}</span>
              </div>
            )}
            {/* {(job?.specialty || job?.taxonomy_specialty) && (
              <div className="meta-item">
                <span className="meta-label">Specialty</span>
                <span className="meta-value">
                  {job?.specialty || job?.taxonomy_specialty}
                </span>
              </div>
            )} */}

            {job?.certifications_required && (
              <div className="meta-item">
                <span className="meta-label">Required Certificate</span>
                <span className="meta-value">
                  {(() => {
                    try {
                      const value = job.certifications_required;

                      if (Array.isArray(value)) {
                        return value.join(", ");
                      }

                      const parsed = JSON.parse(value || "[]");

                      return Array.isArray(parsed)
                        ? parsed.join(", ")
                        : String(parsed);
                    } catch (e) {
                      return String(job.certifications_required);
                    }
                  })()}
                </span>
              </div>
            )}
          </div>

          {/* Location */}

          {/* <div className="job-location">
            <MapPin size={14} />
            {job?.location?.city && job?.location?.state
              ? `${job.location.city}, ${job.location.state}`
              : "Location not specified"}
          </div> */}

          {/* Schedule */}
          {/* <div className="job-schedule">
            <Clock size={14} />
            <span>{timeAgo(job?.posted_date)} </span>
          </div> */}

          <div className="job-schedule tags">
            {job?.work_model && (
              <div className="tag">
                <div className="tag-desc">{job.work_model}</div>
              </div>
            )}
            {job?.shift_type && (
              <div className="tag">
                <div className="tag-desc">{job.shift_type}</div>
              </div>
            )}

            {job?.job_type && (
              <div className="tag">
                <div className="tag-desc">{job.job_type}</div>
              </div>
            )}
          </div>
        </div>

        <div className="buttons pt-0">
          <Button
            className="w-100 btn btn-secondary"
            data-tour={tour ? "jobs-jc-apply" : undefined}
            onClick={async (e) => {
              e.stopPropagation();
              if (!job?.id) return;

              // Record the job view first — server enforces the free-plan
              // daily cap. 403 → upgrade prompt + no navigation.
              try {
                await jobViewService.record(job.id);
              } catch (err) {
                const body = err?.response?.data;
                if (
                  err?.response?.status === 403 &&
                  body?.errorType === "JOB_VIEW_LIMIT_REACHED"
                ) {
                  setJobViewLimitInfo({
                    planSlug: body?.data?.plan_slug || "free",
                    used: body?.data?.used ?? 5,
                    limit: body?.data?.limit ?? 5,
                  });
                  return;
                }
                // Soft-fail anything else (network/auth) — still let the
                // user proceed rather than silently swallow their click.
              }

              const session_id = sessionStorage.getItem(
                "comparison_session_id",
              );
              if (session_id) {
                // Track application click for analytics — separate from the
                // plan-cap accounting handled above.
                dispatch(
                  jobTracking({
                    session_id,
                    event_type: "application_clicked",
                    job_id: job.id,
                    job_title: job?.title || null,
                    specialty: selectedRole || null,
                    job_type: selectedJobType?.[0] || null,
                    shift_type: selectedShiftType?.[0] || null,
                    company_name:
                      job?.company_name || job?.company?.name || null,
                    timestamp: new Date().toISOString(),
                  }),
                );
              }

              // Always open the internal detail page, regardless of source.
              navigate(`/job-detail/${job.id}`);
            }}
          >
            Apply Now
          </Button>

          <Button
            variant="link"
            size="sm"
            onClick={handleShareClick}
            className="d-flex align-items-center gap-2 action-btn text-muted btn btn-secondary"
            data-tour={tour ? "jobs-jc-share" : undefined}
          >
            <Send size={24} />
            {/* <span>Share</span> */}
          </Button>
          {shareModal && (
            <div
              className="modal-backdrop-overlay"
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.6)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                zIndex: 99998,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              // onClick={closeShareModal}
              onClick={(e) => {
                e.stopPropagation(); // ✅ ADD THIS
                closeShareModal();
              }}
            >
              <div
                className="modal-content-wrapper"
                style={{
                  backgroundColor: "var(--mmp-surface)",
                  borderRadius: "12px",
                  maxWidth: "400px",
                  width: "90%",
                  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
                  zIndex: 99999,
                  position: "relative",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "12px",
                    borderBottom: "1px solid var(--mmp-border)",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "var(--mmp-text-primary)",
                      margin: 0,
                      lineHeight: "24px",
                    }}
                  >
                    Share Job
                  </h3>
                  <button
                    onClick={closeShareModal}
                    style={{
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: "4px",
                      borderRadius: "4px",
                      color: "var(--mmp-text-secondary)",
                      fontSize: "24px",
                      lineHeight: 1,
                    }}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>

                {/* Modal Body */}
                <div style={{ padding: "0" }}>
                  {/* Twitter */}
                  <div
                    onClick={() => shareToSocialMedia("twitter")}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "12px 24px",
                      cursor: "pointer",
                      borderBottom: "1px solid #f3f4f6",
                      transition: "background-color 0.2s ease",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "var(--mmp-hover-bg)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    <div
                      style={{
                        width: "22px",
                        height: "22px",
                        backgroundColor: "#1da1f2",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                      }}
                    >
                      <FaXTwitter
                        size={14}
                        style={{
                          display: "block",
                          margin: "0 auto",
                        }}
                      />
                    </div>

                    <span style={{ fontSize: "14px", fontWeight: "500" }}>
                      Share on Twitter
                    </span>
                  </div>

                  {/* Facebook */}
                  <div
                    onClick={() => shareToSocialMedia("facebook")}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "12px 24px",
                      cursor: "pointer",
                      borderBottom: "1px solid #f3f4f6",
                      transition: "background-color 0.2s ease",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "var(--mmp-hover-bg)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    <div
                      style={{
                        width: "22px",
                        height: "22px",
                        backgroundColor: "#1877f2",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        fontSize: "10px",
                        fontWeight: "bold",
                      }}
                    >
                      <FaFacebookF
                        size={14}
                        style={{
                          display: "block",
                          margin: "0 auto",
                        }}
                      />
                    </div>
                    <span style={{ fontSize: "14px", fontWeight: "500" }}>
                      Share on Facebook
                    </span>
                  </div>

                  {/* WhatsApp */}
                  <div
                    onClick={() => shareToSocialMedia("whatsapp")}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "12px 24px",
                      cursor: "pointer",
                      borderBottom: "1px solid #f3f4f6",
                      transition: "background-color 0.2s ease",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "var(--mmp-hover-bg)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    <div
                      style={{
                        width: "22px",
                        height: "22px",
                        backgroundColor: "#25d366",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        fontSize: "12px",
                        fontWeight: "bold",
                      }}
                    >
                      <FaWhatsapp
                        size={14}
                        style={{
                          display: "block",
                          margin: "0 auto",
                        }}
                      />
                    </div>
                    <span style={{ fontSize: "14px", fontWeight: "500" }}>
                      Share on WhatsApp
                    </span>
                  </div>

                  {/* LinkedIn */}
                  <div
                    onClick={() => shareToSocialMedia("linkedin")}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "12px 24px",
                      cursor: "pointer",
                      borderBottom: "1px solid #f3f4f6",
                      transition: "background-color 0.2s ease",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "var(--mmp-hover-bg)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    <div
                      style={{
                        width: "22px",
                        height: "22px",
                        backgroundColor: "#0077b5",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        fontSize: "10px",
                        fontWeight: "bold",
                      }}
                    >
                      <FaLinkedinIn
                        size={14}
                        style={{
                          display: "block",
                          margin: "0 auto",
                        }}
                      />
                    </div>
                    <span style={{ fontSize: "14px", fontWeight: "500" }}>
                      Share on LinkedIn
                    </span>
                  </div>

                  {/* Copy Link */}
                  <div
                    onClick={() => shareToSocialMedia("copy")}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "12px 24px",
                      cursor: "pointer",
                      transition: "background-color 0.2s ease",
                      borderBottomLeftRadius: "12px",
                      borderBottomRightRadius: "12px",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "var(--mmp-hover-bg)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    <Copy
                      style={{
                        width: "20px",
                        height: "20px",
                        color: "var(--mmp-text-secondary)",
                      }}
                    />
                    <span style={{ fontSize: "14px", fontWeight: "500" }}>
                      Copy Link
                    </span>
                  </div>

                  {/* Share to Community */}
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      closeShareModal();
                      const jobContent = generateJobPostContent();
                      setPreFilledContent(jobContent);
                      setShowCreatePostModal(true);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "12px 24px",
                      cursor: "pointer",
                      borderTop: "1px solid var(--mmp-border)",
                      transition: "background-color 0.2s ease",
                      borderBottomLeftRadius: "12px",
                      borderBottomRightRadius: "12px",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "var(--mmp-accent-bg)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    <Users
                      style={{
                        width: "20px",
                        height: "20px",
                        color: "var(--mmp-accent)",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "var(--mmp-accent)",
                      }}
                    >
                      Share to Community
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Post Modal for sharing to community - Rendered outside clickable div */}
      {showCreatePostModal && (
        <CreatePostModal
          show={true}
          onHide={() => {
            setShowCreatePostModal(false);
            setPreFilledContent("");
          }}
          createPost={createPost}
          categories={categories}
          isLoading={categoriesLoading}
          initialCategory={jobListingCategory?.category_name || "Job Listing"}
          preFilledContent={preFilledContent}
          onpostSubmit={() => {}}
          onCategoryChange={() => {}}
        />
      )}

      <RestrictionModal
        isVisible={Boolean(jobViewLimitInfo)}
        onClose={() => setJobViewLimitInfo(null)}
        eventSource="job_view_limit"
        iconType="briefcase"
        heading="You've reached your daily job-view limit"
        subText={`You've viewed your ${jobViewLimitInfo?.limit ??
          5} free jobs for today. Upgrade to Pro for unlimited job views.`}
        infoText={`You're on the ${(jobViewLimitInfo?.planSlug || "free")
          .charAt(0)
          .toUpperCase() +
          (jobViewLimitInfo?.planSlug || "free").slice(
            1,
          )} plan · ${jobViewLimitInfo?.used ??
          0} of ${jobViewLimitInfo?.limit ?? 5} job views used today`}
        thirdCtaMode="newsletter"
      />

      <RestrictionModal
        isVisible={showSaveJobModal}
        onClose={() => setShowSaveJobModal(false)}
        eventSource="save_job"
        iconType="clipboard-dollar"
        thirdCtaMode="newsletter"
        heading="Save more jobs"
        subText="Free users can save 1 job per day. Upgrade to Pro for unlimited saved jobs."
        infoText="Pro users can save unlimited jobs, organize opportunities, and track applications."
      />
    </>
  );
};

export default SimpleJobCard;
